import React, { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { Loader } from '../../components/Loader';
import { Input } from '../../components/Input';
import { Icon } from '../../components/Icon';
import CustomLink from '../../components/CustomLink';
import { createIssueReportLink } from '../../../lib/shared/link';
import { useAuthentication } from '../../shared/use-authentication';
import { useAuthForm } from '../../shared/use-auth-form';
import { useRouting } from '../../shared/use-routing';
import styles from './AuthSignInPage.css';

function AuthSignInPage() {
    const {location, parseLocationSearch} = useRouting();
    const [isLoading, setIsLoading] = useState(true);
    const [isUnvalidLink, setIsUnvalidLink] = useState(false);
    const [isChangedInput, setIsChangedInput] = useState(false);
    const {token} = parseLocationSearch(location.search);
    const {signIn} = useAuthentication();
    const {isPending, isSuccessful, changeField, submitForm, fields, errors} = useAuthForm(isUnvalidLink);

    const handleChangeField = (...props) => {
        if (!isChangedInput) {
            setIsChangedInput(true);
        }

        changeField(...props);
    };

    const authorize = async () => {
        try {
            await signIn(token);
        } catch (e) {
            setIsUnvalidLink(true);

            throw e;
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            authorize();
        }
    }, [token]);

    if (isLoading) {
        return (
            <div className={styles.root}>
                <Loader variant='secondary' />
            </div>
        );
    };

    if (isUnvalidLink) {
        return (
            <div className={styles.wrapper}>
                <form className={styles.form}>
                    <h1 className={styles.title}>Sign in</h1>
                    <div className={styles.formWrapper}>
                        {isSuccessful
                            ? (
                                <>
                                    <Icon name='key' className={styles.successfulIcon} />
                                    <p className={styles.successfulText}>
                                        We've sent a login link to <strong>{fields.email}</strong> — check your inbox and follow it.<br /><br />
                                        <strong>Feel free to close this tab.</strong>
                                    </p>
                                </>
                            )
                            : (
                                <>
                                    <Input
                                        value={fields.email}
                                        name='email'
                                        onChange={handleChangeField}
                                        className={styles.input}
                                        label='Email address'
                                        placeholder='mail@example.com'
                                        errorText={errors.email}
                                        inputProps={{autoComplete: 'email', type: 'email'}}
                                        disabled={isSuccessful || isPending}
                                    />
                                    {!isChangedInput && (
                                        <p className={styles.unvalidText}>
                                            Access denied — the magic link is invalid or expired. <br /><br />
                                            Request a new one.
                                        </p>
                                    )}
                                    {Boolean(errors.form) && (
                                        <p className={styles.error}>{errors.form}</p>
                                    )}
                                    <Button
                                        label='Continue'
                                        onClick={submitForm}
                                        className={styles.button}
                                        isLoading={isPending}
                                        disabled={isSuccessful}
                                    />
                                </>       
                            )
                        }
                    </div>
                    <p className={styles.issueReportText}>
                        Having trouble or need help signing in? <br />
                        <CustomLink
                            to={createIssueReportLink()}
                        >
                            Report an issue
                        </CustomLink>
                    </p>
                </form>
            </div>
        )
    };

    return (
        <div className={styles.root}>
            <Loader variant='secondary' />
        </div>
    );
}

export {
    AuthSignInPage
}
